'use client';

import React, { useMemo } from 'react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useTranslation } from 'react-i18next';
import type { ModelFormProps } from '@/features/workspace/modelFormTypes';
import PromptFieldReadonly from '@/features/workspace/components/PromptFieldReadonly';
import { createInitialValues, type SoraRemixFormValues } from './types';

const SoraRemixForm: React.FC<ModelFormProps<SoraRemixFormValues>> = ({
  values,
  onChange,
  onSubmit,
  busy,
  error,
  messages,
  taskListSlot,
}) => {
  const { t } = useTranslation(['pages', 'components']);
  const formValues = useMemo(() => {
    const initial = createInitialValues();
    if (!values) {
      return initial;
    }
    return {
      ...initial,
      ...values,
      videoId: typeof values.videoId === 'string' ? values.videoId : initial.videoId,
      prompt: typeof values.prompt === 'string' ? values.prompt : initial.prompt,
    };
  }, [values]);
  const promptValue = formValues.prompt;
  const videoIdValue = formValues.videoId;

  const disableSubmit =
    busy || !promptValue.trim() || !videoIdValue.trim();

  const submitLabel = busy
    ? t('pages:modelWorkspace.actions.submitting', '提交中…')
    : t('components:soraRemix.submit', '提交编辑');
  const promptReadonlyHint = t('pages:modelWorkspace.promptDockOnlyHint', {
    defaultValue: '提示词请在 Prompt Dock 中编辑。',
  });

  const handleVideoIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...formValues, videoId: event.target.value });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (disableSubmit) {
      return;
    }
    void onSubmit(formValues);
  };

  return (
    <Card className="h-full">
      <form onSubmit={handleSubmit} className="flex h-full flex-col">
        <CardContent className="flex min-h-0 flex-1 flex-col gap-3">
          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1">
            {error ? <Alert variant="destructive">{error}</Alert> : null}
            {messages?.map((message, index) => (
              <Alert key={`sora-remix-message-${index}`}>{message}</Alert>
            ))}

            <div className="flex flex-col gap-1.5">
              <div className="text-sm font-semibold">
                {t('components:soraRemix.videoIdLabel', { defaultValue: '待编辑视频 ID' })}
              </div>
              <Input
                value={videoIdValue}
                onChange={handleVideoIdChange}
                placeholder={t('components:soraRemix.videoIdPlaceholder', {
                  defaultValue: '例如：video_abcdef123456',
                }) as string}
                disabled={busy}
              />
              <p className="text-xs text-muted-foreground">
                {t('components:soraRemix.videoIdHelper', {
                  defaultValue: '会自动填入最终成片的任务 ID，也可手动指定其他可用视频。',
                })}
              </p>
            </div>

            <PromptFieldReadonly
              label={t('components:soraRemix.promptLabel', { defaultValue: '编辑提示词' })}
              helperText={promptReadonlyHint}
              minRows={6}
            />
          </div>

          <div className="mt-auto flex items-center gap-2">
            {taskListSlot ? <div className="min-w-0 flex-1">{taskListSlot}</div> : null}
            <Button type="submit" disabled={disableSubmit}>
              {submitLabel}
            </Button>
          </div>
        </CardContent>
      </form>
    </Card>
  );
};

export default SoraRemixForm;
